import React, { useState, useCallback, useMemo } from 'react'
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme, useNavigation, useRoute, RouteProp } from '@react-navigation/native'
import { Button, Icon, Screen, Text } from '@components'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import {
  useFtGetSocialImportStatus,
  useFtGetSocialImportResultsPages,
  socialImportResultsTotal,
  useFtValidateSocialImport,
} from '@hooks'
import { translateApiError } from '@utils'
import type { ScrapedProfile } from '@hooks'
import { ScrapedProfilesList } from './components/ScrapedProfilesList'
import { notify } from './notify'
import type { ContactStackParamList } from '../../../navigation/navigator/ContactNavigator'

// Profiles sent per request when validating: a large selection in one request
// would not get its answer in time.
const VALIDATE_BATCH_SIZE = 50

const networkLabels: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'Twitter/X',
  linkedin: 'LinkedIn',
}

export const FtSocialImportResults = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const navigation = useNavigation()
  const route = useRoute<RouteProp<ContactStackParamList, 'FtSocialImportResults'>>()
  const { importId } = route.params

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isValidating, setIsValidating] = useState(false)
  // Usernames added by a validation that stopped half-way: shown as already added.
  const [addedUsernames, setAddedUsernames] = useState<string[]>([])

  const { data: statusData } = useFtGetSocialImportStatus(importId)
  const {
    data: resultsData,
    isLoading: isLoadingResults,
    error: resultsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFtGetSocialImportResultsPages(importId, statusData?.status === 'succeeded')
  const validateImport = useFtValidateSocialImport()

  const network = statusData?.network ?? ''
  const networkLabel = networkLabels[network] ?? network
  const isRunning = statusData?.status === 'running' || statusData?.status === 'pending'
  const isSucceeded = statusData?.status === 'succeeded'
  const isFailed = statusData?.status === 'failed' || statusData?.status === 'timed_out'

  // Pages are only ever added at the end, so the position of a profile in this
  // list (what the selection holds) never changes.
  const profiles = useMemo(
    () => (resultsData?.pages ?? []).flatMap((page) => page.items),
    [resultsData?.pages],
  )
  const alreadyImportedUsernames = useMemo(
    () =>
      new Set([
        ...(resultsData?.pages ?? []).flatMap((page) => page.alreadyImportedUsernames ?? []),
        ...addedUsernames,
      ]),
    [resultsData?.pages, addedUsernames],
  )
  // The size of the import: the number of the previous screens, unless the
  // results announce more.
  const total = socialImportResultsTotal(statusData?.totalResults, resultsData?.pages[0]?.total)
  const hasMore = hasNextPage && profiles.length < total
  // Only when nothing can be shown: a later page that fails keeps the list.
  const resultsFailed = !!resultsError && profiles.length === 0

  const selectableProfiles = useMemo(
    () => profiles.filter((p) => !(p.username && alreadyImportedUsernames.has(p.username))),
    [profiles, alreadyImportedUsernames],
  )

  const handleToggleProfile = useCallback(
    (index: number) => {
      const profile = profiles[index]
      if (profile.username && alreadyImportedUsernames.has(profile.username)) return

      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(index)) {
          next.delete(index)
        } else {
          next.add(index)
        }
        return next
      })
    },
    [profiles, alreadyImportedUsernames],
  )

  const handleSelectAll = useCallback(() => {
    const selectableIndices = profiles
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => !(p.username && alreadyImportedUsernames.has(p.username)))
      .map(({ i }) => i)

    if (selectedIds.size === selectableIndices.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(selectableIndices))
    }
  }, [selectedIds.size, profiles, alreadyImportedUsernames])

  const handleValidate = useCallback(async () => {
    if (selectedIds.size === 0) return

    const selectedIndexes = Array.from(selectedIds)
    const selectedProfiles = selectedIndexes.map((index) => {
      const p = profiles[index]
      return {
        fullName:
          p.fullName ||
          [p.firstName, p.lastName].filter(Boolean).join(' ') ||
          p.username ||
          'Unknown',
        username: p.username ?? undefined,
        avatarUrl: p.avatarUrl ?? undefined,
        bio: p.bio ?? undefined,
        profileUrl: p.profileUrl ?? undefined,
      }
    })

    setIsValidating(true)
    let done = 0
    try {
      for (; done < selectedProfiles.length; done += VALIDATE_BATCH_SIZE) {
        await validateImport.mutateAsync({
          importId,
          selectedProfiles: selectedProfiles.slice(done, done + VALIDATE_BATCH_SIZE),
        })
      }
      notify(
        'success',
        t('ftSocialImport.results.successTitle'),
        t('ftSocialImport.results.successMessage', { count: selectedProfiles.length }),
      )
      navigation.goBack()
    } catch (e: unknown) {
      const { message } = translateApiError(t, e)
      if (done > 0) {
        // The first batches went through: take them out of the selection and
        // mark them as added, so trying again does not create them twice.
        const sentIndexes = selectedIndexes.slice(0, done)
        setSelectedIds((prev) => {
          const next = new Set(prev)
          sentIndexes.forEach((index) => next.delete(index))
          return next
        })
        setAddedUsernames((prev) => [
          ...prev,
          ...sentIndexes.map((index) => profiles[index].username).filter((u): u is string => !!u),
        ])
      }
      notify(
        'error',
        t('ftSocialImport.error.title'),
        done > 0 ? `${message}\n${t('ftSocialImport.results.partial', { count: done })}` : message,
      )
    } finally {
      setIsValidating(false)
    }
  }, [importId, selectedIds, profiles, validateImport, navigation, t])

  return (
    <Screen>
      <View style={styles.header}>
        <Icon name="arrowBack" onPress={() => navigation.goBack()} />
        <Text type="titleBold" color={theme.colors.black}>
          {t('ftSocialImport.results.title', { network: networkLabel })}
        </Text>
        <View style={{ width: 24 }} />
      </View>

      {isRunning && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text type="body" color={theme.colors.secondary} style={styles.loadingText}>
            {t('ftSocialImport.inProgress')}
          </Text>
          <Text type="bodyExtraSmall" color={theme.colors.lightGrey}>
            {t('ftSocialImport.results.runningHint')}
          </Text>
        </View>
      )}

      {isFailed && (
        <View style={styles.loadingContainer}>
          <Text type="body" color={theme.colors.error}>
            {t('ftSocialImport.results.failedTitle')}
          </Text>
          <Text type="bodyExtraSmall" color={theme.colors.lightGrey}>
            {statusData?.errorMessage || t('ftSocialImport.error.genericError')}
          </Text>
        </View>
      )}

      {isSucceeded && resultsFailed && (
        <View style={styles.loadingContainer}>
          <Text type="body" color={theme.colors.error} style={styles.message}>
            {translateApiError(t, resultsError).message}
          </Text>
        </View>
      )}

      {isSucceeded && !resultsFailed && (
        <View style={styles.resultsContainer}>
          <View style={styles.resultsHeader}>
            <View>
              <Text type="body" color={theme.colors.secondary}>
                {t('ftSocialImport.profilesFound', { count: total })}
              </Text>
              {hasMore && (
                <Text type="bodyExtraSmall" color={theme.colors.lightGrey}>
                  {t('ftSocialImport.results.shownOf', { shown: profiles.length, total })}
                </Text>
              )}
            </View>
            <TouchableOpacity onPress={handleSelectAll}>
              <Text type="body" color={theme.colors.primary}>
                {selectedIds.size === selectableProfiles.length && selectableProfiles.length > 0
                  ? t('ftSocialImport.results.deselectAll')
                  : t('ftSocialImport.results.selectAll')}
              </Text>
            </TouchableOpacity>
          </View>

          {isLoadingResults ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
            </View>
          ) : profiles.length === 0 ? (
            <View style={styles.loadingContainer}>
              <Text type="body" color={theme.colors.secondary} style={styles.message}>
                {t('ftSocialImport.results.empty')}
              </Text>
            </View>
          ) : (
            <ScrapedProfilesList
              profiles={profiles}
              selectedIds={selectedIds}
              onToggle={handleToggleProfile}
              disabledUsernames={alreadyImportedUsernames}
              onLoadMore={fetchNextPage}
              hasMore={hasMore}
              isLoadingMore={isFetchingNextPage}
            />
          )}

          <View style={styles.footer}>
            <Button
              title={t('ftSocialImport.results.validate', { count: selectedIds.size })}
              onPress={handleValidate}
              disabled={selectedIds.size === 0 || isValidating}
              containerStyle={styles.button}
            />
          </View>
        </View>
      )}
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.md,
    },
    loadingContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.md,
    },
    loadingText: {
      marginTop: theme.spacing.sm,
    },
    message: {
      textAlign: 'center',
      paddingHorizontal: theme.spacing.lg,
    },
    resultsContainer: {
      flex: 1,
    },
    resultsHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.sm,
    },
    footer: {
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
    },
    button: {
      marginTop: 0,
    },
  })
